import { FundingSchedule } from '@src/components/FundingSchedule';
import { navSidebarMessages } from '@src/components/NavigationSidebar';
import { envConfig } from '@src/utils/envConfig';
import {
  Box,
  Button,
  Divider,
  FormControlSizes,
  FormattedMessage,
  Icon,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventSource,
  NavTab,
  SymbolDisplay,
  SymbolSelector,
  SymbolSelectorAppearance,
  TabList,
  TabSize,
  Tabs,
  ToggleButton,
  WL_ACTION,
  getIntlKey,
  selectIsFormOpen,
  setOpenForm,
  themes,
  useIntl,
  useMixpanel,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
  useWLOrgConfigContext,
  useWLRoleAuth,
  useWLSymbol,
  type CustomerUserConfigNavigationItem,
  type IntlWithFormatter,
  type ThemeTypes,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { defineMessages } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, type DefaultTheme } from 'styled-components';
import { useNavigationItems } from '../../../hooks/useNavigationItems';
import { CreditUtilization } from '../../CreditUtilization';
import { Logo } from '../../Logo';
import { useCurrentLayoutComponent } from '../useCurrentLayoutComponent';
import { UserMenu } from './UserMenu';
import { HEADER_CONTENT_HEIGHT, HeaderContent, NavigationHeaderWrapper, Section } from './styles';

const messages = defineMessages({
  orderForm: {
    id: 'Layout.NavigationHeader.orderForm',
    defaultMessage: 'Order Form',
  },
  support: {
    id: 'Layout.NavigationHeader.support',
    defaultMessage: 'Support',
  },
});

interface NavigationHeaderProps {
  showSymbolSelector: boolean;
  themeType: ThemeTypes;
  maxWidth?: number;
  compact: boolean;
  sidebarToggle: boolean;
  showDemoSettings: boolean;
  drawerOpen: boolean;
  openDrawer?: () => void;
  openDemoModal?: () => void;
  openSupportModal?: () => void;
  onChangeThemeType: (themeType: ThemeTypes) => void;
}

export function NavigationHeader({
  showSymbolSelector,
  maxWidth,
  themeType,
  compact,
  sidebarToggle,
  drawerOpen,
  showDemoSettings,
  openDrawer,
  openDemoModal,
  openSupportModal,
  onChangeThemeType,
}: NavigationHeaderProps) {
  const { config } = useWLOrgConfigContext();
  const formsOpen = useWLOrderFormSelector(selectIsFormOpen);
  const dispatch = useWLOrderFormDispatch();
  const setFormsOpen = useCallback(
    (isOpen: boolean) => {
      dispatch(setOpenForm(isOpen));
    },
    [dispatch]
  );
  const { symbol, setSymbol } = useWLSymbol();
  const { isAuthorized } = useWLRoleAuth();
  const navigate = useNavigate();
  const layoutComponent = useCurrentLayoutComponent();
  const intl = useIntl();

  const tabItems = useNavigationItems();
  const menuItems = config.menuItems;

  const mixpanel = useMixpanel();

  const handleSymbolChange = useCallback(
    (symbol?: string) => {
      mixpanel.track(MixpanelEvent.ChangeSymbol, { source: MixpanelEventSource.Header });
      if (symbol) {
        setSymbol(symbol);
      }
    },
    [mixpanel, setSymbol]
  );

  const location = useLocation();
  const selectedIndex = tabItems.findIndex(ti => ti['path'] && ti['path'] === location.pathname);

  const hasNavItems = tabItems.length > 1 || (menuItems?.length ?? 0) > 0;

  const isProd = envConfig.VITE_AVA_ENV === 'prod';

  const theme = useTheme();
  const headerTheme = ((theme.headerThemeName && (themes as any)[theme.headerThemeName]) || theme) as DefaultTheme;

  return (
    <ThemeProvider theme={headerTheme}>
      <NavigationHeaderWrapper pb={hasNavItems && !compact && !sidebarToggle ? 0 : undefined}>
        <HeaderContent maxWidth={hasNavItems ? undefined : maxWidth}>
          {sidebarToggle && (
            <Box ml={!maxWidth ? '-spacing12' : ''}>
              <IconButton
                ghost
                disabled={drawerOpen}
                onClick={openDrawer}
                icon={IconName.Menu}
                size={FormControlSizes.Large}
                color="colorTextImportant"
              />
            </Box>
          )}
          <Section onClick={() => navigate('/')}>
            <Logo themeType={theme.type} height="32px" />
          </Section>
          {layoutComponent.showOrderFormToggle === 'left' && (
            <>
              <Divider orientation="vertical" my="spacing12" />
              <Section>
                <ToggleButton
                  size={FormControlSizes.Small}
                  selected={formsOpen}
                  onClick={() => setFormsOpen(!formsOpen)}
                >
                  <FormattedMessage {...messages.orderForm} />
                </ToggleButton>
              </Section>
            </>
          )}
          {showSymbolSelector ? (
            <>
              <Divider orientation="vertical" my="spacing12" />
              <Section alignItems="start">
                {!isAuthorized(WL_ACTION.USE_SYMBOL_SELECTOR) ? (
                  <SymbolDisplay symbol={symbol} />
                ) : (
                  <SymbolSelector
                    appearance={SymbolSelectorAppearance.Ghost}
                    symbol={symbol}
                    onSymbolChanged={handleSymbolChange}
                    dropdownWidth="200px"
                  />
                )}
              </Section>
            </>
          ) : null}

          {hasNavItems && compact && (
            <>
              <Divider orientation="vertical" my="spacing12" />
              <Section>
                <NavigationTabs selectedIndex={selectedIndex} tabItems={tabItems} intl={intl} />
              </Section>
            </>
          )}
          <Section ml="auto">
            <CreditUtilization />
          </Section>

          {sidebarToggle && (layoutComponent.showOrderFormToggle === 'right' || config.orderFormToggle) && (
            <Section>
              <ToggleButton selected={formsOpen} onClick={() => setFormsOpen(!formsOpen)}>
                <FormattedMessage {...messages.orderForm} />
              </ToggleButton>
            </Section>
          )}
          {!sidebarToggle && (
            <Section>
              <Button prominent size={FormControlSizes.Small} startIcon={IconName.Support} onClick={openSupportModal}>
                <FormattedMessage {...messages.support} />
              </Button>
              <FundingSchedule />
              {showDemoSettings && (
                <IconButton
                  size={FormControlSizes.Small}
                  prominent
                  icon={IconName.PresentationChartBar}
                  onClick={openDemoModal}
                />
              )}
              {(layoutComponent.showOrderFormToggle === 'right' || config.orderFormToggle) && (
                <ToggleButton
                  selected={formsOpen}
                  onClick={() => setFormsOpen(!formsOpen)}
                  size={FormControlSizes.Small}
                >
                  <FormattedMessage {...messages.orderForm} />
                </ToggleButton>
              )}
              <UserMenu
                themeType={themeType}
                onChangeThemeType={onChangeThemeType}
                showThemeToggle={config?.allowThemeChange !== false}
                showLayoutToggle={!tabItems && (!isProd || !!config?.allowLayoutChange)}
              />
            </Section>
          )}
        </HeaderContent>
        {hasNavItems && !compact && !sidebarToggle && (
          <NavigationTabs selectedIndex={selectedIndex} tabItems={tabItems} intl={intl} />
        )}
      </NavigationHeaderWrapper>
    </ThemeProvider>
  );
}

function NavigationTabs({
  selectedIndex,
  tabItems,
  intl,
}: {
  selectedIndex: number;
  tabItems: CustomerUserConfigNavigationItem[];
  intl: IntlWithFormatter;
}) {
  const { spacing8 } = useTheme();
  return (
    <Tabs pt={spacing8} selectedIndex={selectedIndex} size={TabSize.Large}>
      <TabList suppressOverflowList={true} h={HEADER_CONTENT_HEIGHT + spacing8}>
        {tabItems.map(ti => {
          const isExternal = ti.path.startsWith('//') || ti.path.startsWith('http');
          const intlKey = getIntlKey(ti.label);
          const label = isNavSideBarMessage(intlKey) ? intl.formatMessage(navSidebarMessages[intlKey]) : ti.label;
          return (
            <NavTab
              key={ti.label}
              label={label}
              to={ti.path}
              suffix={isExternal && ti.icon && <Icon icon={ti.icon} />}
              target={isExternal ? (ti.target ?? '_blank') : undefined}
              rel={isExternal ? 'noreferrer' : undefined}
            />
          );
        })}
      </TabList>
    </Tabs>
  );
}

const isNavSideBarMessage = (key: string): key is keyof typeof navSidebarMessages => key in navSidebarMessages;
